/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { parseBrtDateTime } from '@pages/authenticated/agency-pages/visits/visit-time-conflict';

/**
 * Whether a visit is still ahead of now.
 *
 * The rule the Visitas board has always applied before letting a visit
 * be marked done: a visit that has not happened yet cannot be recorded
 * as having happened. The attendance screen grew its own "marcar como
 * realizada" without it, which made the attendance a way around the
 * board's own guard.
 *
 * Parsed as BRT and compared with the local clock, matching the board
 * exactly -- two readings of "is it in the future" that disagreed would
 * be worse than one that is merely approximate.
 *
 * A visit with no date is not in the future: there is no moment to be
 * ahead of, and refusing it would make an undated record impossible to
 * settle at all.
 *
 * @param visitDatetime - The visit's stored datetime.
 * @returns True when the visit is scheduled after this moment.
 */
export function isVisitInFuture(visitDatetime?: string | null): boolean {
  if (!visitDatetime) return false;
  const { date, time } = parseBrtDateTime(visitDatetime);
  return new Date(`${date}T${time}:00`) > new Date();
}

/**
 * A visit's moment, written the way the Visitas board writes it.
 *
 * The stored `visit_datetime` is wall-clock time already -- the board
 * reads it through the UTC getters precisely so the browser's own zone
 * never shifts it. Formatted through `toLocaleString` instead, a visit
 * booked for 09:00 rendered as 06:00 on the attendance screen: the same
 * visit, three hours apart, depending on which screen you opened.
 *
 * @param isoString - The stored datetime.
 * @returns The moment as `DD/MM/YYYY, HH:mm`.
 */
export function formatVisitMoment(isoString: string): string {
  const { date, time } = parseBrtDateTime(isoString);
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}, ${time}`;
}
